import appLogger from '@shared/logging/appLogger';
import {
    MinimalVehicleView,
    ResultItem,
    Results,
    SearchBody,
    ViewRequest,
} from '@shared/types/search';
import { ShoppingApiHost } from '@shared/utilities/endpoints';

export async function getSuggestions(
    text: string,
    getToken: () => Promise<string>
): Promise<string[]> {
    const url = new URL(`${ShoppingApiHost}/api/auctions/search/autocomplete`);
    url.searchParams.append('searchTerm', text);

    return fetch(url.toString(), {
        headers: {
            Authorization: `Bearer ${await getToken()}`,
        },
    }).then(response => {
        if (response.ok) {
            return response.json() as unknown as string[];
        } else {
            appLogger.logTrace({
                message: 'Unable to get suggestions for text',
                properties: {
                    text,
                    responseCode: response.status,
                    response: JSON.stringify(response),
                    requestUrl: url.toString(),
                },
            });

            // Return empty array on error
            return [] as string[];
        }
    });
}

interface VehicleDetailQuery {
    getToken: () => Promise<string>;
    stockNum?: string;
    itemId?: string;
    vin?: string;
}

export async function getVehicleDetails({
    getToken,
    stockNum,
    itemId,
    vin,
}: VehicleDetailQuery): Promise<ResultItem | undefined> {
    const params = new URLSearchParams();
    if (stockNum) {
        params.append('stockNum', stockNum);
    }
    if (itemId) {
        params.append('itemId', itemId);
    }
    if (vin) {
        params.append('vin', vin);
    }

    const url = new URL(`${ShoppingApiHost}/api/auctions/vehicle?${params.toString()}`);

    return fetch(url.toString(), {
        method: 'GET',
        headers: {
            Authorization: `Bearer ${await getToken()}`,
        },
    })
        .then(response => {
            if (response.ok) {
                return response.json();
            }

            throw new Error(`${response.status}: ${response.statusText}`);
        })
        .catch(err => {
            let error = err as Error;
            if (!error) {
                error = new Error(err);
            }
            const logError = { error, properties: { requestUrl: url.toString() } };
            appLogger.logException(logError);
            return [];
        });
}

export async function lookupVehicles(
    stockNumbers: string[],
    getToken: () => Promise<string>
): Promise<ResultItem[]> {
    const url = new URL(`${ShoppingApiHost}/api/auctions/vehicle/batch/complete`);
    return fetch(url.toString(), {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${await getToken()}`,
        },
        body: JSON.stringify([...stockNumbers]),
    })
        .then(response => {
            if (response.ok) {
                return response.json();
            }

            throw new Error(`${response.status}: ${response.statusText}`);
        })
        .catch(err => {
            let error = err as Error;
            if (!error) {
                error = new Error(err);
            }
            const logError = { error, properties: { requestUrl: url.toString() } };
            appLogger.logException(logError);
            return [];
        });
}

export async function recordView(
    viewRequest: ViewRequest,
    getToken: () => Promise<string>
): Promise<void> {
    const url = new URL(`${ShoppingApiHost}/api/views/increment`);

    return fetch(url.toString(), {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${await getToken()}`,
        },
        body: JSON.stringify({ ...viewRequest }),
    })
        .then(response => {
            if (response.ok) {
                return;
            }

            throw new Error(`${response.status}: ${response.statusText}`);
        })
        .catch(err => {
            let error = err as Error;
            if (!error) {
                error = new Error(err);
            }
            const logError = { error, properties: { requestUrl: url.toString() } };
            appLogger.logException(logError);
            return;
        });
}

export async function getViews(getToken: () => Promise<string>): Promise<MinimalVehicleView[]> {
    const url = new URL(`${ShoppingApiHost}/api/views`);

    return fetch(url.toString(), {
        headers: {
            Authorization: `Bearer ${await getToken()}`,
        },
    })
        .then(response => {
            if (response.ok) {
                return response.json();
            }

            throw new Error(`${response.status}: ${response.statusText}`);
        })
        .catch(err => {
            let error = err as Error;
            if (!error) {
                error = new Error(err);
            }
            const logError = { error, properties: { requestUrl: url.toString() } };
            appLogger.logException(logError);
            return [];
        });
}

export async function search(body: SearchBody, getToken: () => Promise<string>): Promise<Results> {
    const url = new URL(`${ShoppingApiHost}/api/v2/auctions/search`);
    return fetch(url.toString(), {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${await getToken()}`,
        },
        body: JSON.stringify({ ...body }),
    })
        .then(response => {
            if (response.ok) {
                return response.json();
            }

            throw new Error(`${response.status}: ${response.statusText}`);
        })
        .catch(err => {
            let error = err as Error;
            if (!error) {
                error = new Error(err);
            }
            const logError = { error, properties: { requestUrl: url.toString() } };
            appLogger.logException(logError);
            return {
                cacheKey: '',
                count: 0,
                results: [],
                total: 0,
            } as Results;
        });
}

export async function printSearch(
    body: SearchBody,
    getToken: () => Promise<string>
): Promise<Blob> {
    const url = new URL(`${ShoppingApiHost}/api/v2/auctions/search`);
    try {
        const response = await fetch(url.toString(), {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${await getToken()}`,
            },
            body: JSON.stringify(body),
        });

        if (response.ok) {
            return response.blob(); // Return the response as a Blob
        }
        throw new Error(`${response.status}: ${response.statusText}`);
    } catch (err) {
        let error = err as Error;
        if (!error) {
            error = new Error(err);
        }
        const logError = { error, properties: { requestUrl: url.toString() } };
        appLogger.logException(logError);
        // Return an empty Blob in case of an error
        return new Blob();
    }
}

/** Relates the backend key to display name of the facet. */
export interface FacetName {
    /** The key used for referencing the facet in the backend.
     * Ex: 'auctionevents'
     */
    key: string;
    /** The display name of the facet in the UI.
     * Ex: 'Auctions'
     */
    name: string;
}

export async function getFacetNames(getToken: () => Promise<string>): Promise<FacetName[]> {
    const url = new URL(`${ShoppingApiHost}/api/auctions/search/facets`);
    return fetch(url.toString(), {
        headers: {
            Authorization: `Bearer ${await getToken()}`,
        },
    })
        .then(response => {
            if (response.ok) {
                return response.json();
            }

            throw new Error(`${response.status}: ${response.statusText}`);
        })
        .catch(err => {
            let error = err as Error;
            if (!error) {
                error = new Error(err);
            }
            const logError = { error, properties: { requestUrl: url.toString() } };
            appLogger.logException(logError);
            return [];
        });
}

/** Shortcut to look up facets by name and value. Ex: facets['makes']['Audi'] */
export interface FacetLookup {
    [value: string]: {
        [value: string]: Facet;
    };
}

export interface Facet {
    /** ex: 'models' */
    name: string;
    /** ex: 'Acura' */
    value: string;
    /** Count of how many vehicles with this facet */
    score: number;
    children?: Facet[];
    childrenLookup?: FacetLookup;
}

export async function getFacets(
    parentFacetName: string,
    getToken: () => Promise<string>,
    childFacetName?: string
): Promise<Facet[]> {
    const url = new URL(
        `${ShoppingApiHost}/api/auctions/search/facets/${parentFacetName}/${childFacetName}`
    );
    return fetch(url.toString(), {
        headers: {
            Authorization: `Bearer ${await getToken()}`,
        },
    })
        .then(response => {
            if (response.ok) {
                return response.json();
            }

            throw new Error(`${response.status}: ${response.statusText}`);
        })
        .catch(err => {
            let error = err as Error;
            if (!error) {
                error = new Error(err);
            }
            const logError = { error, properties: { requestUrl: url.toString() } };
            appLogger.logException(logError);
            return [];
        });
}
